import React, { useCallback, useEffect, useState } from 'react';
import type { DehydratedState } from 'react-query';
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import type { AppState } from '@auth0/auth0-react';
import { Auth0Provider } from '@auth0/auth0-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import type { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import { ResultsProvider } from '@context/ResultsContext';
import type { EmotionCache } from '@emotion/react';
import { CacheProvider } from '@emotion/react';
import { GoogleApiProvider } from '@goodfynd/context';
import { SnackbarProvider } from '@goodfynd/context';
import { strings } from '@goodfynd/strings';
import { createEmotionCache, ThemeProvider } from '@goodfynd/theme';
import { DialogProvider } from '@goodfynd/ui';
import Head from 'next/head';
import { useRouter } from 'next/router';

import '@goodfynd/react-web/theme';

import { Layout } from '../components/layout/Layout';
import config from '../config';
import env from '../config/env';
import routes from '../config/routes';
import { AppProvider } from '../context/AppContext';
import { OrganizationProvider } from '../context/OrganizationContext';
import { VendorProvider } from '../context/VendorContext';
import type { RootStore } from '../stores';
import { DefaultState, useRootStore } from '../stores';
import { RootStoreProvider } from '../stores/root-store/root-store-context';

import "@goodfynd/react-web/global.css";
import '@goodfynd/react-web/theme/fonts/fonts.css';
import './global-css/global.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

Bugsnag.start({
  apiKey: config.env.BUGSNAG_API_KEY,
  enabledReleaseStages: [config.env.NAME.toLowerCase()],
  plugins: [new BugsnagPluginReact()],
  releaseStage: config.env.NAME.toLowerCase(),
});
BugsnagPerformance.start({ apiKey: config.env.BUGSNAG_API_KEY });

import BugsnagPluginReact from '@bugsnag/plugin-react';
import { LayoutDashboard } from '@components/layout/Layout/LayoutDashboard';
import Loading from '@components/Loading';
import { getDashboardRoutePath } from '@utils/routes-util';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { ParsedUrlQuery } from 'querystring';

import HeadScripts from './_head-scripts';
if (config.app.mode.isProduction) {
  console.debug = () => console.info();
  console.error = () => console.info();
  console.log = () => console.info();
  console.warn = () => console.info();
}

const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(
  React
) as BugsnagErrorBoundary;

export type NextPageWithLayout = NextPage & {
  getLayout?: (
    page: React.ReactElement,
    query: ParsedUrlQuery
  ) => React.ReactNode;
};

type AppPropsWithLayout = AppProps<{ dehydratedState: DehydratedState }> & {
  emotionCache?: EmotionCache;
  Component: NextPageWithLayout;
};

function MyApp({
  Component,
  pageProps: { dehydratedState, ...pageProps },
}: AppPropsWithLayout): JSX.Element | null {
  const [emotionCache, setEmotionCache] = useState<EmotionCache>();
  const createRootStore = useRootStore();
  const router = useRouter();

  const [enRoute, toggleRouting] = useState(false);

  useEffect(() => {
    // Client-side cache, shared for the whole session of the user in the browser.
    setEmotionCache(createEmotionCache());
  }, []);

  // Configure/load anything that needs to be ready before site is displayed to user
  // ***NOTE: Not sure if this is the right way to handle for the web
  // ***NOTE RESPONSE Anthony: No this is causing horrible loading times, our SEO is going to be shit. My initial load time before cache kicks in is like 5s */
  useEffect(() => {
    (async () => {
      setRootStore(await createRootStore()); // configure up mobx-state-tree
      // Now wait for state tree to be ready
    })();
  }, [createRootStore]);

  const queryClient = React.useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
          },
        },
      }),
    []
  );

  const [rootStore, setRootStore] = useState<RootStore>(DefaultState);

  const handleRouteChange = useCallback(() => {
    toggleRouting(false);
  }, []);

  const handleRouteChangeStart = useCallback((url: URL) => {
    const nextUrl = new URL(url, window.location.origin);
    if (nextUrl.pathname != window.location.pathname) {
      toggleRouting(true);
    }
  }, []);

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [handleRouteChange, handleRouteChangeStart, router.events]);

  const onRedirectCallback = (appState?: AppState) => {
    router.replace(
      appState?.returnTo ||
        getDashboardRoutePath(routes.dashboard.overview, '~')
    );
  };

  const renderWithContext = useCallback(
    (children: React.ReactNode) => {
      if (!router.isReady) {
        return <Loading type="line" />;
      }

      if (router.asPath.includes('/beta')) {
        return (
          <OrganizationProvider key="beta">{children}</OrganizationProvider>
        );
      }

      return (
        <OrganizationProvider key="legacy">
          <VendorProvider key="legacy">{children}</VendorProvider>
        </OrganizationProvider>
      );
    },
    [router.asPath, router.isReady]
  );

  // Wait for state to be ready
  if (emotionCache && rootStore) {
    const getLayout =
      Component.getLayout ??
      ((page: React.ReactNode) => {
        if (router.asPath.includes('/beta')) {
          return <LayoutDashboard>{page}</LayoutDashboard>;
        }

        return <Layout>{page}</Layout>;
      });
    return (
      <>
        <ErrorBoundary>
          <ThemeProvider>
            <Auth0Provider
              clientId={process.env.NEXT_PUBLIC_CLIENT_ID || ''}
              domain={process.env.NEXT_PUBLIC_DOMAIN || ''}
              onRedirectCallback={onRedirectCallback}
              useRefreshTokens={true}
              useRefreshTokensFallback={true}
              authorizationParams={{
                scope: 'openid email offline_access',
                audience: process.env.NEXT_PUBLIC_AUDIENCE,
                redirect_uri:
                  (typeof window !== 'undefined' &&
                    window.location.origin + routes.account.callback) ||
                  '',
              }}
            >
              <RootStoreProvider value={rootStore}>
                <QueryClientProvider client={queryClient}>
                  <Hydrate state={dehydratedState}>
                    <CacheProvider value={emotionCache}>
                      <DialogProvider>
                        <SnackbarProvider>
                          <Head>
                            <title>{strings.app.name}</title>
                            <meta
                              content="initial-scale=1, width=device-width"
                              name="viewport"
                            />
                          </Head>

                          <GoogleApiProvider apiKey={env.GOOGLE_API_KEY}>
                            <AppProvider>
                              <ResultsProvider>
                                {enRoute && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      zIndex: 9999,
                                    }}
                                  >
                                    <Loading type="line" />
                                  </div>
                                )}
                                {renderWithContext(
                                  getLayout(
                                    <Component {...pageProps} />,
                                    router.query
                                  )
                                )}
                              </ResultsProvider>
                            </AppProvider>
                          </GoogleApiProvider>
                        </SnackbarProvider>
                      </DialogProvider>
                    </CacheProvider>
                  </Hydrate>

                  {!config.app.mode.isProduction && (
                    <ReactQueryDevtools position="bottom-right" />
                  )}
                </QueryClientProvider>
              </RootStoreProvider>
            </Auth0Provider>
          </ThemeProvider>
        </ErrorBoundary>
        <HeadScripts />
      </>
    );
  }

  return null;
}

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// export async function getServerSideProps(context) {
//   return {
//     props: {
//       name: 'Lemaire S.',
//     }, // will be passed to the page component as props
//   };
// }

export default MyApp;
